/** Conteúdo em português. Os nomes dos modos seguem o catálogo `pt.json`. */
import type { LanguageContent } from '../registry.js';

export const PT: LanguageContent = {
  home: {
    intro: [
      'Retusch é um jogo de desenho online para 2 a 10 jogadores, jogável no navegador, sem instalar nada e sem conta. Cada rodada entrega um quadro com um quadrado apagado: repinte-o para que ele desapareça dentro da obra. Depois os papéis se invertem, e é preciso descobrir os retoques dos outros.',
      'A maioria dos jogos de desenho premia quem é reconhecido mais rápido. Retusch pede o contrário: vencer é não ser visto.',
    ],
    steps: [
      {
        title: 'Um quadro com um buraco',
        text: 'Você recebe uma ilustração com um quadrado apagado.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Você repinta',
        text: 'Você redesenha o quadrado para que ele se funda na obra.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'A caça',
        text: 'O quadro volta com os retoques de todos: encontre-os antes dos outros.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'A revelação',
        text: 'Cada esconderijo é revelado, e os pontos caem.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Caça coletiva',
        text: 'Todo mundo pinta no mesmo quadro, cada um no seu quadrado. Depois a caça abre para todos ao mesmo tempo: um retoque só é encontrado uma vez, por quem o avista primeiro. É o modo padrão, aquele em que sempre está acontecendo alguma coisa.',
      },
      {
        name: 'Stream',
        text: 'O anfitrião não desenha: ele procura, sozinho, enquanto todos os outros se escondem no quadro dele. Pensado para a transmissão ao vivo, onde a caça vira o espetáculo. Grátis para sempre.',
      },
      {
        name: 'Um quadro para cada',
        text: 'Uma obra por jogador. Você só procura o retoque de uma pessoa, em um único clique. A regra mais simples do jogo, e a mais tensa.',
      },
      {
        name: 'Só um desenha',
        text: 'Uma rodada, um desenhista, todos os outros atrás dele. O papel gira, e o desenhista vê o próprio esconderijo cair ou resistir.',
      },
      {
        name: 'Battle royale',
        text: 'A mesma caça, mas o primeiro retoque descoberto elimina o seu autor. Joga-se até o último de pé, sem contar um único ponto.',
      },
      {
        name: 'Concurso',
        text: 'Ninguém procura. Todo mundo repinta o mesmo quadrado do mesmo quadro, e depois cada um vota no retoque que mais gostou. Um voto recebido vale um ponto.',
      },
    ],
    faq: [
      {
        question: 'Quantos jogadores são necessários?',
        answer:
          'De 2 a 10. Se você estiver sozinho, um adversário automático ocupa a cadeira vazia para que a partida comece na hora. A partir de quatro, a caça coletiva mostra toda a sua graça.',
      },
      {
        question: 'É grátis?',
        answer:
          'Sim. A partida rápida, a caça coletiva e o modo stream são gratuitos, sem limite de tempo nem de número de partidas. Os outros modos também são, por enquanto.',
      },
      {
        question: 'Preciso criar uma conta?',
        answer:
          'Não. Você digita um apelido, clica e joga. A conta serve apenas para guardar o seu apelido e o seu avatar de uma partida para outra: continua opcional.',
      },
      {
        question: 'Funciona no celular?',
        answer:
          'Sim, a interface se adapta ao toque: barra de ferramentas recolhida, lupa sob o dedo, toque duplo para ampliar. Dito isso, desenha-se com mais precisão no mouse e numa tela grande.',
      },
      {
        question: 'Como convido meus amigos?',
        answer:
          'Crie uma sala, copie o link de convite e cole onde quiser. Seus amigos chegam direto à mesa, sem conta e sem instalar nada.',
      },
      {
        question: 'O que é um «retoque»?',
        answer:
          'É o que você pinta dentro do quadrado apagado. Bem feito, passa por um pedaço do quadro original e ninguém o encontra. Mal feito, salta aos olhos.',
      },
    ],
    headings: {
      intro: 'Um jogo de desenho em que é preciso desaparecer',
      steps: 'Como jogar',
      modes: 'Seis maneiras de jogar',
      faq: 'Perguntas frequentes',
      more: 'Para saber mais',
    },
    links: {
      rules: 'Ler as regras completas e a pontuação',
      modes: 'Comparar os modos em detalhe',
    },
  },

  howToPlay: {
    title: 'Como jogar Retusch',
    summary:
      'As regras completas de Retusch: como se desenrola uma rodada, a pontuação dos dois lados, as ferramentas de desenho e as dicas para se esconder e para procurar.',
    sections: [
      {
        heading: 'A ideia em três frases',
        blocks: [
          'Você recebe um quadro com um quadrado apagado, e o repinta para que passe despercebido.',
          'Depois todo mundo olha os quadros retocados e procura os quadrados repintados.',
          'Você pontua encontrando rápido, e pontua também enquanto o seu próprio esconderijo resistir.',
        ],
      },
      {
        heading: 'Como se desenrola uma rodada',
        blocks: [
          'Uma partida encadeia rodadas idênticas, cinco por padrão. Cada uma passa por três momentos.',
          {
            list: [
              'O desenho. Você recebe o seu quadro e o seu quadrado, e tem 2 minutos por padrão para repintá-lo. O botão Concluído vale como envio: assim que todos enviarem, a rodada avança sem esperar o relógio.',
              'A caça. O quadro reaparece com os retoques, e você tem 30 segundos por padrão para localizá-los. Um clique num quadrado repintado o revela para a mesa inteira.',
              'A revelação. Todos os esconderijos aparecem emoldurados e com o nome do autor, inclusive os que ninguém viu. Um cursor permite comparar o quadro retocado com a obra original.',
            ],
          },
        ],
      },
      {
        heading: 'A pontuação',
        blocks: [
          'Dois contadores correm ao mesmo tempo, e se opõem: quem procura quer ir rápido, quem desenhou quer que o seu quadrado resista.',
          {
            list: [
              'Do lado de quem procura: 1000 pontos se encontrar nos 3 primeiros segundos, e depois uma queda regular até 100 pontos no fim da fase. Errar ou não encontrar nada não rende nada.',
              'Do lado de quem desenha: apenas 100 pontos se o seu quadrado cair na hora, e depois uma subida regular até 800 pontos se ele resistir até o fim. Um retoque que ninguém encontra vale o máximo, 800 pontos.',
            ],
          },
          'O desenhista é pago uma única vez, na revelação, e com base em quem o encontrou mais rápido. Tanto faz, portanto, se duas ou oito pessoas estão procurando você: só conta quem o encontra primeiro.',
          'A pontuação é a mesma qualquer que seja a duração definida para a fase. Alongar a caça não move os dois limites, apenas a inclinação entre eles.',
          'Um único erro por rodada. O primeiro clique que não toca nenhum retoque encerra a sua caça: sem essa regra, varreríamos a imagem ao acaso até esbarrar em algo. Dois gestos não custam nada: clicar no seu próprio quadrado, e clicar num retoque já encontrado.',
        ],
      },
      {
        heading: 'As ferramentas de desenho',
        blocks: [
          {
            list: [
              'O pincel (B): três tamanhos rápidos, além de um controle para o tamanho exato. Os atalhos 1, 2 e 3 escolhem os três tamanhos.',
              'O balde (P): preenche de uma vez uma superfície lisa, sem nunca sair do seu quadrado.',
              'O conta-gotas (I): pega uma cor em qualquer ponto do quadro. Segurar Alt muda para ele por um clique e devolve a ferramenta anterior em seguida.',
              'A mão (H): desloca a vista. Fora do seu quadrado, o pincel e o balde fazem o mesmo, então você quase nunca precisa trocar de ferramenta.',
              'Desfazer (Ctrl+Z ou Cmd+Z): volta traço a traço, até trinta vezes. Não há refazer.',
              'A cor de fundo, no fim da paleta: é a cor inicial do seu quadrado. Pintar com ela equivale a apagar, com a diferença de que é um traço como outro qualquer, que se desfaz e se cobre. Por isso não existe borracha.',
            ],
          },
        ],
      },
      {
        heading: 'Cinco dicas para se esconder',
        blocks: [
          {
            list: [
              'Pegue as cores, não as adivinhe. O conta-gotas dá a cor exata da vizinhança: um tom levemente deslocado se nota de longe, enquanto o tom certo fica invisível.',
              'Prolongue o que atravessa o seu quadrado. Um muro, um horizonte, um galho que entra de um lado tem de sair do outro no lugar certo. Uma linha interrompida é a primeira pista que um jogador experiente procura.',
              'Pinte os cantos primeiro. É o erro mais comum: o centro fica caprichado, os quatro cantos permanecem com o fundo original, e o quadrado aparece a olho nu sem que seja preciso olhar o desenho.',
              'Vá do geral ao detalhe. Coloque primeiro as grandes massas de cor com um pincel grosso, e só depois refine. O contrário consome todo o seu tempo num canto enquanto o resto fica vazio.',
              'Copie o nível de detalhe do entorno. Um quadrado limpo demais no meio de uma área carregada aparece tanto quanto um malfeito no meio de um tom liso.',
            ],
          },
        ],
      },
      {
        heading: 'Quatro dicas para procurar',
        blocks: [
          {
            list: [
              'Procure as linhas que param. Uma borda, um cabo, a quina de uma mesa que se desloca um fio: é quase sempre ali.',
              'Varra primeiro, amplie depois. Na vista inteira, um quadrado mal fundido aparece como uma mancha. O zoom serve para confirmar, não para procurar.',
              'Desconfie do liso demais. Uma superfície perfeitamente uniforme no meio de uma textura é tão suspeita quanto um borrão.',
              'Não clique ao acaso. Você só tem direito a um erro: melhor perder três segundos conferindo do que perder a rodada inteira.',
            ],
          },
        ],
      },
      {
        heading: 'E conforme o modo?',
        blocks: [
          'Tudo o que vem acima descreve a caça coletiva, o modo padrão. Os outros modos mudam quem desenha, quem procura e como se vence: o detalhe de cada um está na página dos modos de jogo.',
        ],
      },
    ],
  },

  modes: {
    title: 'Os modos de jogo de Retusch',
    summary:
      'As seis maneiras de jogar Retusch: caça coletiva, stream, um quadro para cada, só um desenha, battle royale e concurso. Como funciona cada uma, número de jogadores recomendado e duração.',
    sections: [
      {
        heading: 'Caça coletiva',
        blocks: [
          'O modo padrão, e o melhor para começar.',
          'Todo mundo desenha no mesmo quadro, cada um no seu próprio quadrado. Quando os retoques são entregues, a obra volta inteira e a caça abre para todos ao mesmo tempo. Um retoque só é encontrado uma vez, por quem o avista primeiro: ele então aparece para a mesa inteira e sai do jogo. Você nunca encontra o seu, e um esconderijo que ninguém vê rende o máximo ao autor.',
          'Ideal para um grupo grande: não há tempo morto, todos procuram ao mesmo tempo e cada descoberta aparece na tela de todos.',
          'Recomendado: de 3 a 10 jogadores.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'A imagem invertida do modo «só um desenha», feita para a transmissão ao vivo.',
          'Todos os jogadores desenham no mesmo quadro, menos o anfitrião. Depois o anfitrião é o único a procurar, enquanto o público o assiste se esforçar. Ele não soma pontos e não aparece na classificação: a partida se joga entre os que se escondem, ele conduz a caça. Pode errar quantas vezes quiser, a regra do erro único não vale para ele.',
          'Ideal para uma noite ao vivo, e por um motivo preciso: como o anfitrião não desenha, os espectadores nunca veem o esconderijo dele sendo construído. Nos outros modos, eles o veem pintar o próprio quadrado, e a caça deixa de ter segredo.',
          'Recomendado: de 4 a 10 jogadores. Grátis, e vai continuar assim.',
        ],
      },
      {
        heading: 'Um quadro para cada',
        blocks: [
          'Cada jogador recebe a sua própria obra e esconde nela o seu retoque. Na hora da caça, você recebe o quadro de outra pessoa e tem um único clique para apontar o quadrado repintado. Sem corrida nem concorrência: só você, um quadro e uma decisão.',
          'Ideal em dupla, ou quando se quer uma partida calma em que cada um leva o tempo que precisa.',
          'Recomendado: de 2 a 10 jogadores.',
        ],
      },
      {
        heading: 'Só um desenha',
        blocks: [
          'Uma rodada, um desenhista, todos os outros atrás dele.',
          'A cada rodada, uma única pessoa recebe um quadro. Os outros esperam e depois procuram o retoque dela, cada um pontuado pelo próprio tempo. O desenhista é pontuado por quem o encontrou mais rápido, e revê o seu esconderijo congelado enquanto o procuram. O papel gira: aqui o ajuste de rodadas conta voltas completas, porque uma partida só é justa se cada um desenhou o mesmo número de vezes.',
          'Ideal para um grupo pequeno que queira se julgar em pé de igualdade, um desenho de cada vez.',
          'Recomendado: de 3 a 6 jogadores, acima disso a espera fica longa.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'A caça coletiva, com uma eliminação por rodada.',
          'Mesmo quadro compartilhado, mesma caça simultânea. Mas aqui não se conta nenhum ponto: a única questão é quem fica. O jogador cujo retoque é descoberto primeiro deixa a partida. Os outros continuam procurando até o fim da rodada, o que já não elimina ninguém mas dá a cada um a sua colocação, mostrada sobre o seu quadrado. Se ninguém for encontrado, ninguém sai.',
          'Ideal para encerrar uma noite: a tensão sobe conforme a mesa esvazia, e a última rodada é um duelo.',
          'Recomendado: de 4 a 10 jogadores.',
        ],
      },
      {
        heading: 'Concurso',
        blocks: [
          'O único modo em que ninguém procura.',
          'Todo mundo repinta o mesmo quadrado do mesmo quadro. Depois cada um percorre os retoques dos outros, no seu ritmo, e vota naquele que achou melhor. Um voto recebido vale um ponto ao autor. Durante a votação os autores são anônimos, e não se pode votar em si mesmo: os nomes e os votos só saem na revelação.',
          'Ideal quando o grupo é misto, ou quando alguns preferem desenhar sem a pressão do relógio da caça.',
          'Recomendado: de 3 a 10 jogadores.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Caça coletiva', players: '3 a 10', duration: '15 a 20 min' },
    { name: 'Stream', players: '4 a 10', duration: '15 a 20 min' },
    { name: 'Um quadro para cada', players: '2 a 10', duration: '15 a 20 min' },
    { name: 'Só um desenha', players: '3 a 6', duration: '20 a 35 min' },
    { name: 'Battle royale', players: '4 a 10', duration: '20 a 30 min' },
    { name: 'Concurso', players: '3 a 10', duration: '10 a 15 min' },
  ],
  modeTableHeadings: ['Modo', 'Jogadores', 'Duração'],
  premium: {
    heading: 'Modos gratuitos e modos pagos',
    blocks: [
      'A caça coletiva e o modo stream são gratuitos, definitivamente.',
      'Os outros quatro passarão a ser pagos ao CRIAR uma partida, e apenas ao criá-la. Entrar continuará sempre grátis: basta que uma pessoa da mesa tenha desbloqueado o jogo para que todos possam jogar todos os modos, sem conta e sem pagar.',
      'O desbloqueio será uma compra única, sem assinatura, e também tirará a publicidade da conta que o fez. Enquanto isso, todos os modos estão abertos a todo mundo.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'O que é Retusch?',
      date: '2026-08-21',
      summary:
        'Um jogo de desenho online para vários, próximo de Gartic Phone na forma, mas cuja mecânica lembra a de Meccha Chameleon: em vez de se fazer reconhecer, procura-se desaparecer.',
      sections: [
        {
          heading: 'Um jogo de desenho em que ser reconhecido é perder',
          blocks: [
            'Retusch é um jogo de desenho online para 2 a 10 jogadores. Cada rodada entrega uma ilustração com um quadrado apagado, e você precisa repintá-lo para que se funda na obra. Depois todo mundo olha os quadros retocados e procura os quadrados repintados.',
            'O que o distingue cabe em uma frase: vencer é não ser visto.',
          ],
        },
        {
          heading: 'O que ele tem em comum com Gartic Phone',
          blocks: [
            'A forma é a mesma: você abre um link, digita um apelido e joga. Sem conta, sem instalação, sem cliente para baixar. O jogo roda no navegador e uma partida começa em segundos. Convidam-se os amigos colando um link numa conversa, e a mesa se enche.',
            'O ritmo também é familiar: rodadas curtas, um cronômetro, uma fase de desenho e depois uma fase em que se descobre o que os outros produziram. E sobretudo aquela revelação coletiva no fim da rodada em que todo mundo comenta o que vê. É o mesmo prazer de grupo.',
            'Como em Gartic Phone, não é preciso saber desenhar. Um quadrado de céu bem colocado vence uma obra-prima que transborda.',
          ],
        },
        {
          heading: 'O que ele toma emprestado de Meccha Chameleon',
          blocks: [
            'No fundo, porém, a mecânica está em outro lugar. Em Meccha Chameleon, jogo em três dimensões na Steam, você pinta o seu próprio personagem e depois o coloca num cenário esperando que ninguém o distinga do que está ao redor. A vitória vem da semelhança e do bom posicionamento, não da ação.',
            'Retusch retoma essa ideia e a traz de volta para a imagem. Você não escolhe onde se esconder, é o jogo que lhe dá o quadrado; em compensação, você fabrica inteiramente o que vai dentro. O esconderijo funciona se o olhar escorregar por cima sem parar.',
            'E como em todo jogo de camuflagem, a tensão vem do papel duplo. Você está escondido e caçando ao mesmo tempo: enquanto examina o quadro em busca de um tom suspeito, alguém examina o seu.',
          ],
        },
        {
          heading: 'O que não existe em nenhum outro lugar',
          blocks: [
            'A pontuação premia os dois lados de forma oposta. Quem procura pontua mais quanto antes encontra; quem desenhou pontua mais quanto mais o seu quadrado resiste. Um retoque que ninguém vê rende o máximo ao autor.',
            'Daí nasce um jeito de jogar muito particular: não se busca fazer bonito, busca-se fazer exato. Pegar a cor certa, prolongar uma linha no lugar certo, não deixar nenhum canto vazio. São gestos de observação antes de serem gestos de desenho.',
            'Para entender na prática, o mais simples é ler as regras completas, ou abrir uma partida rápida: ela começa sem ajustes e sem espera.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Como ser imbatível em Retusch',
      date: '2026-08-21',
      summary:
        'Os atalhos de teclado, o método do geral para o detalhe e o erro dos cantos vazios: três coisas que separam um iniciante de um jogador que ninguém mais encontra.',
      sections: [
        {
          heading: 'Aprenda os atalhos e você dobra o seu tempo útil',
          blocks: [
            'Dois minutos de desenho passam muito rápido quando se busca cada ferramenta com o mouse. Os atalhos recuperam facilmente vinte ou trinta segundos por rodada, e são esses segundos que você vai gastar nos detalhes.',
            {
              list: [
                'B para o pincel, P para o balde, I para o conta-gotas, H para a mão.',
                '1, 2 e 3 para os três tamanhos de pincel, sem passar pela barra de ferramentas.',
                'Ctrl+Z, ou Cmd+Z no Mac, para desfazer traço a traço.',
                'Segurar Alt muda para o conta-gotas e devolve a ferramenta anterior assim que você solta. É o mais útil de todos: você pega uma cor em plena ação sem nunca trocar de ferramenta.',
              ],
            },
            'Uma observação sobre teclados AZERTY: as teclas 1, 2 e 3 funcionam sem Shift, mesmo que o seu teclado escreva ali &, é e ". O jogo lê a posição da tecla, não o caractere que ela produz.',
          ],
        },
        {
          heading: 'Pinte do geral para o detalhe',
          blocks: [
            'O erro clássico do iniciante é começar por um canto e caprichar nele. O cronômetro acaba, três quartos do quadrado continuam sendo fundo, e o retoque se vê a dez metros.',
            'O método certo é o inverso. Comece com um pincel grosso, ou com o balde, e coloque as grandes massas de cor: o céu, a parede, a sombra no chão. Em vinte segundos o seu quadrado já tem a aparência geral correta e deixa de parecer um bloco estranho.',
            'Só então reduza o tamanho e trate as transições: a linha do horizonte que atravessa, a borda de um objeto, o tom que muda na metade da altura. Se faltar tempo, você terá perdido detalhe, não a rodada.',
            'Pegue sempre as suas cores com o conta-gotas em vez de escolhê-las na paleta. Um tom aproximado basta para entregar um quadrado inteiro, enquanto o tom exato o torna invisível.',
          ],
        },
        {
          heading: 'Nunca deixe os cantos vazios',
          blocks: [
            'É a pista número um, e a primeira que os jogadores experientes procuram. Um quadrado com o centro bem tratado mas com os quatro cantos na cor de fundo desenha um retângulo perfeito na tela. Nem é preciso olhar o desenho: a forma geométrica salta aos olhos.',
            'Acostume-se a tratar primeiro o contorno e depois o centro. Uma moldura bem fundida perdoa um interior aproximado; o contrário nunca é verdade.',
            'Ultrapasse de propósito as bordas. O jogo corta o seu traço no limite do quadrado, então você não arrisca nada indo bem além, e é a única forma de garantir que nenhum pixel do fundo sobreviva num canto.',
          ],
        },
        {
          heading: 'E quando é a sua vez de procurar',
          blocks: [
            'Varra primeiro o quadro inteiro sem ampliar: um quadrado mal fundido parece uma mancha, e o zoom só serve para confirmar. Depois siga as linhas longas, as que atravessam a imagem: uma borda deslocada um fio quase sempre é um retoque.',
            'E lembre-se de que você só tem direito a um erro por rodada. Três segundos conferindo custam menos que a rodada inteira.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Um jogo de desenho para jogar com amigos',
      date: '2026-08-21',
      summary:
        'Por que Retusch funciona bem em grupo pequeno, como convidar colando um link, e por que basta um jogador para desbloquear os modos pagos para a mesa inteira.',
      sections: [
        {
          heading: 'Um jogo que não exige nada de ninguém',
          blocks: [
            'O maior obstáculo de uma noite de jogos à distância é a instalação. Uma conta para criar, um cliente para baixar, uma versão que não bate: metade do grupo desiste antes de jogar.',
            'Retusch roda no navegador. Seus amigos clicam num link, digitam um apelido e se encontram na mesa. Não há nada para instalar, nada para criar, e funciona tão bem no computador quanto no celular.',
            'Outra coisa que conta em grupo: ninguém fica de fora por não saber desenhar. O jogo não pede para representar nada, apenas para fazer um quadrado combinar com o que o cerca. Quem desenha mal mas observa bem ganha com frequência.',
          ],
        },
        {
          heading: 'Convidar, na prática',
          blocks: [
            'Crie uma sala pela tela inicial. Desmarque a caixa «sala pública» se quiser ficar só entre vocês: o convite por link vira então o único caminho de entrada.',
            'Copie o link com o botão previsto e cole na sua conversa de grupo, no seu canal de voz ou no seu mensageiro. Ele contém o código da sala: seus amigos caem direto na sua mesa.',
            'Você pode ajustar a partida enquanto o pessoal chega: o modo, o número de rodadas, o tempo de desenho, o tamanho do quadrado e até as categorias de imagens. Nada fica travado antes de você começar.',
          ],
        },
        {
          heading: 'Um paga, todos jogam',
          blocks: [
            'Dois modos são grátis para sempre: a caça coletiva, que é o modo padrão, e o modo stream.',
            'Os outros passarão a ser pagos, mas só ao CRIAR uma partida. Entrar continuará grátis, sem condições. Na prática, basta que uma única pessoa do grupo tenha desbloqueado o jogo para que a mesa inteira aproveite todos os modos, sem conta e sem pagar nada.',
            'O desbloqueio é uma compra única, sem assinatura.',
          ],
        },
        {
          heading: 'Qual modo para qual grupo',
          blocks: [
            'Em três ou quatro, a caça coletiva basta e se entende em uma rodada. A partir de seis ela fica realmente boa: as descobertas se encadeiam e a tela não para de se mexer.',
            'Para encerrar a noite, experimente o battle royale: a mesa esvazia rodada após rodada e a última é um duelo. Para um grupo misto, em que alguns preferem desenhar com calma, o concurso tira toda a pressão do relógio da caça.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Um jogo de desenho para jogar em live',
      date: '2026-08-21',
      summary:
        'Retusch tem um modo pensado para a transmissão ao vivo, em que o streamer procura sozinho enquanto o público se esconde. Como usá-lo, e que outros modos funcionam diante de uma audiência.',
      sections: [
        {
          heading: 'Um modo feito para isso',
          blocks: [
            'O modo stream inverte os papéis. Todos os jogadores desenham no mesmo quadro, menos o anfitrião, que não desenha nada. Depois o anfitrião é o único a procurar, enquanto o público assiste.',
            'É exatamente a situação que uma live pede, e por um motivo preciso: como o anfitrião não desenha, o esconderijo dele não existe, e o chat não viu nada que não devesse ver. Os espectadores conhecem, em compensação, os esconderijos dos outros jogadores, e podem vê-lo passar ao lado três vezes seguidas.',
            'Duas regras foram escritas para este caso. O streamer não soma pontos e não aparece na classificação: a partida se joga entre os que se escondem, ele conduz o espetáculo. E pode errar quantas vezes quiser, enquanto todos os outros modos param no primeiro erro. Uma live não pode terminar num clique infeliz.',
            'O modo stream é grátis, e vai continuar assim.',
          ],
        },
        {
          heading: 'O concurso, a segunda boa ideia',
          blocks: [
            'No modo concurso, todo mundo repinta o mesmo quadrado do mesmo quadro, e depois cada um vota no retoque que prefere. Não há mais nada para encontrar, apenas para julgar.',
            'Diante de uma audiência funciona muito bem, e pelo mesmo motivo do modo stream: não há nada para encontrar, então não há nada que o chat possa entregar. As propostas se comparam lado a lado, a votação dá um momento de suspense, e a revelação dos nomes oferece um fecho limpo. É também o modo mais curto.',
          ],
        },
        {
          heading: 'E os outros modos',
          blocks: [
            'Nada obriga a jogar um modo dedicado, mas aí é melhor jogar com amigos do que com o chat. Em todos os outros modos o streamer desenha o próprio quadrado na tela: os espectadores o veem sendo construído e sabem a resposta antes mesmo de a caça começar.',
            'Uma partida entre amigos, por outro lado, se acompanha muito bem de fora: a fase de desenho mostra cada um construindo o seu esconderijo, e a revelação descobre todos os quadrados de uma vez, com o nome do autor.',
          ],
        },
        {
          heading: 'Trazer o chat para dentro da partida',
          blocks: [
            'O link de convite é a única coisa a compartilhar. Cole no chat e os primeiros a chegar enchem a mesa: sem conta para criar, sem download e sem fila.',
            'O limite de jogadores se ajusta na sala, entre 2 e 10. Com uma audiência grande, abra várias partidas curtas em vez de uma longa: uma rodada dura menos de três minutos.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'As novidades de Retusch',
      date: '2026-08-21',
      summary:
        'As mudanças de jogo feitas em Retusch, da mais recente à mais antiga: novos modos, novas regras, melhorias no desenho e na caça.',
      sections: [
        {
          heading: 'Agosto de 2026',
          blocks: [
            {
              list: [
                'O quadrado a repintar não parte mais de um quadrado branco, mas da cor dominante daquilo que ele cobre. Não se perde mais metade do tempo repintando um fundo que não existe no quadro.',
                'O balde de tinta não deixa mais um contorno claro junto aos traços: agora ele desliza sob a borda suavizada.',
                'O battle royale não conta mais pontos. O primeiro jogador descoberto deixa a partida, e cada um recebe uma colocação de rodada mostrada sobre o seu quadrado, do vermelho ao verde.',
                'O modo stream dá ao streamer quantas tentativas ele quiser: o primeiro erro não encerra mais a rodada.',
                'No modo espectador já é possível deslocar e ampliar o quadro que se está vendo ser construído.',
                'As reações em emoji saem do apelido do autor, na lista de jogadores, em vez de subirem da parte de baixo da tela.',
                'Fora do quadrado a repintar, o pincel e o balde deslocam a vista: não é mais preciso pegar a ferramenta mão para se locomover.',
                'O nome do jogo, no canto superior esquerdo durante uma partida, leva de volta ao início após confirmação.',
                'O japonês e o coreano se juntam aos quinze idiomas já disponíveis.',
              ],
            },
          ],
        },
        {
          heading: 'Julho de 2026',
          blocks: [
            {
              list: [
                'Novo modo concurso: todo mundo repinta o mesmo quadrado, e depois cada um vota no retoque que prefere.',
                'Novo modo stream, pensado para a transmissão ao vivo: o anfitrião procura sozinho enquanto o público se esconde.',
                'As categorias de imagens substituem os níveis de dificuldade. Marcam-se as que se quer ver, e cada categoria mostra um quadro de exemplo.',
                'Duas rodadas seguidas não caem mais no mesmo quadro, e as categorias marcadas se alternam de uma rodada para outra.',
                'A galeria de fim de partida permite rever um retoque traço a traço e exportá-lo como imagem ou como GIF animado.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Num relance',
    allArticles: 'Todos os artigos',
    theRules: 'As regras do jogo',
    theModes: 'Os modos de jogo',
  },
};
